import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography, Grid } from '@mui/material';
import {makeStyles} from '@mui/styles';
import { trackEvent, GACommonEventAction, GAEventCategory } from 'services/googleAnalytics';
import { COLORS } from 'theme';
import { linkHasAdminAccess, ADMIN_LINK_CHANNEL_KEY } from 'utils/link';
import { Link } from 'types/Link';
import { SimpleSelect } from 'components';
import { useSelectedLinkContext, useWebUserContext, useTourContext } from 'contexts';
import ChannelLinkLabel from './ChannelLinkLabel';

export const getLinkLabel = (link: Link): ReactNode => {
  const firstName = link?.mainUser?.firstName;
  const lastName = link?.mainUser?.lastName;

  const baseLabel = `${firstName} ${lastName}`;

  if (!link.metadata.subscriptionEnabled) {
    return (
      <>
        {baseLabel} (<FormattedMessage id="channelLink.subscriptionDisabled" />)
      </>
    );
  }

  return baseLabel;
};

const useStyles = makeStyles(() => ({
  container: {
    height: 42,
  },
  fullName: {
    fontWeight: 'bold',
  },
  select: {
    height: 42,

    '& .MuiSelect-select': {
      color: COLORS.WHITE,
      fontSize: 18,
      fontWeight: 'bold',
      lineHeight: 'auto',
      paddingLeft: 10
    },
    '& .MuiSelect-icon': {
      color: COLORS.WHITE,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
}), { name: 'AdminChannelLinkSelect' });

const AdminChannelLinkSelect = (): JSX.Element | null => {
  const { webUser } = useWebUserContext();
  const { selectedLink, handleSelectedLinkChange } = useSelectedLinkContext();
  const { isTourOpen } = useTourContext();
  const classes = useStyles();

  const webUserLinks = webUser.links.filter(
    (link) =>
      link.channelKey !== ADMIN_LINK_CHANNEL_KEY && linkHasAdminAccess(link),
  );

  if (isTourOpen) {
    return null;
  }

  if (webUserLinks.length === 0 || !selectedLink) {
    return (
      <ChannelLinkLabel
        label={
          <Typography variant='h5' color='common.white'>
            <FormattedMessage id="channelLink.noWebUserLink" />
          </Typography>
        }
      />
    );
  }

  if (webUserLinks.length === 1) {
    return (
      <Grid className={classes.container} container alignItems='center'>
        <Typography variant='h5' color='common.white'><FormattedMessage id="channelLink.adminManageTablet" />&nbsp;</Typography>
        <Typography className={classes.fullName} variant='h5' color='common.white'>{getLinkLabel(selectedLink)}</Typography>
      </Grid>
    );
  }

  const options = webUserLinks.map((link) => ({
    disabled: !link.metadata.subscriptionEnabled,
    fullValue: link,
    label: getLinkLabel(link),
    value: link.channelKey,
  }));

  const handleChange = (val: Link): void => {
    trackEvent(
      GAEventCategory.CHANNEL_LINK,
      GACommonEventAction.SELECT,
      'Sélectionner une tablette',
    );
    handleSelectedLinkChange({ isAdmin: true, newSelectedLink: val });
  };

  return (
    <Grid className={classes.container} container alignItems='center'>
      <Typography variant='h5' color='common.white'><FormattedMessage id="channelLink.adminManageTablet" /></Typography>

      <SimpleSelect
        className={classes.select}
        value={selectedLink.channelKey}
        options={options}
        handleChange={handleChange}
      />
    </Grid>
  );
};

export default AdminChannelLinkSelect;
